import React from 'react';

import { testimonialOptions } from '../../components/testimoninal/testimoninal';
import { TextWithHighlight } from '../../components/textWithHighlight/textWithHighlight';
import { getEnv } from '../../lib/utils/getEnv';
import { BusinessCompetitor } from '../businessCompetitors/businessCompetitors';
import { SignUpProcessTitleContainer } from '../businessCompetitors/components/signUpProcess/signUpProcess.styled';

const { VITE_WEB_BASE_URL } = getEnv();

const tuftHeroConfig = {
  title: 'Looking to switch from Tuft to a more powerful booking software?',
  subTitle:
    'Join hundreds of UK professionals making the move to Pata, with unlimited access to flexible features designed to simplify and grow your pet care business.',
};

const highlevelBenefitsConfig = [
  {
    id: 'software-without-limits',
    title: 'Software without limits',
    icon: 'Rocket',
    description: 'Manage every appointment, service, and location in one place with room to grow.',
  },
  {
    id: 'reduce-no-show',
    title: 'Reduce no-shows',
    icon: 'NoShow',
    description: 'Minimise cancellations and keep your diary full thanks to reminders and upfront payments.',
  },
  {
    id: 'reduce-no-show',
    title: 'Easy-to-use design',
    icon: 'Tap',
    description: 'View client information at a glance using an intuitive calendar that simplifies admin.',
  },
];

const TuftTestimoninalConfig = [
  {
    children: testimonialOptions.pet_palace,
    color: '#f5c14f',
  },
  {
    children: testimonialOptions.whitegates_grooming,
    color: '#f86e6a',
  },
  {
    children: testimonialOptions.groomed_by_nic,
    color: '#90cfd1',
  },
  {
    children: testimonialOptions.mias_paw_spa,
    color: '#f5a046',
  },
  {
    children: testimonialOptions.pawfect_place,
    color: '#8cc4b8',
  },
];

export const tuftFeaturesTiles = [
  {
    id: 'payments',
    title: 'Integrated payments',
    imageUrl: 'images/image-lady-with-dog-using-laptop.jpeg',
    imageAltTag: 'Lady with a dog using a laptop',
    icon: 'OnlineBooking',
    callout:
      'Offer flexibility for clients without the extra admin for your grooming business — just one account for bookings, deposits, and in-person payments.',
    features: [
      {
        id: 'upfront_payments',
        title: 'Upfront payments',
        description:
          'Reduce no-shows and late cancellations by taking partial deposits or full payments for services during booking, protecting your time.',
      },
      {
        id: 'card_on_file',
        title: 'Card on file',
        description:
          'Securely store payment details to provide a smoother checkout experience for clients and cover yourself for any future cancellation or no-show fees.',
      },
      {
        id: 'terminal',
        title: 'Terminal',
        description:
          'Take payments in person with a Pata card reader that seamlessly links to your account, providing real-time tracking for every transaction.',
      },
      {
        id: 'late_cancellation',
        title: 'Late cancellation and no-show policies',
        description:
          'Set clear terms for when sessions can be cancelled and charge fees if those conditions aren’t met, keeping you in control of your income.',
      },
    ],
  },
  {
    id: 'marketing',
    title: 'Marketing',
    icon: 'Calendar',
    imageAltTag: 'Laptop with a calendar app open',
    imageUrl: 'images/image-laptop-with-calendar-app.png',
    callout:
      'Connect with all your customers, not just those with an app, through one centralised booking platform that helps pet groomers promote and grow their businesses.',
    features: [
      {
        id: 'campaigns',
        title: 'Email and SMS campaigns',
        description:
          'Send branded messages in minutes, straight from your Pata account — keeping clients updated and building excitement to drive sales.',
      },
      {
        id: 'marketplace',
        title: 'Pata Marketplace',
        description:
          'Improve your online presence with a free Marketplace profile and get discovered by pet parents searching for local grooming services.',
      },
      {
        id: 'reviews',
        title: 'Reviews',
        description:
          'Send automated requests for reviews while you focus on the next furry client, showcasing your skills and building trust with new customers.',
      },
    ],
  },
  {
    id: 'catalogue',
    title: 'Supercharged catalogue',
    icon: 'Catalogue',
    imageAltTag: 'Brown dog getting their face trimmed',
    imageUrl: 'images/image-dog-getting-face-trim.jpeg',
    callout:
      'Drive growth in your business by promoting products and services on top of your standard catalogue, configured exactly how you want it.',
    features: [
      {
        id: 'service',
        title: 'Services',
        description:
          'Run your business your way with highly configurable services, including block times, booking limitations, flexible pricing options, and more.',
      },
      {
        id: 'memberships',
        title: 'Memberships',
        description:
          'Build loyalty with clients and enjoy some guaranteed revenue, offering service bundles or promotions that encourage multi-session bookings.',
      },
      {
        id: 'products',
        title: 'Products',
        description:
          'Inspire pet parents to take a slice of the salon home by upselling products you know and love through the same platform they already enjoy.',
      },
      {
        id: 'add-ons',
        title: 'Add on',
        description:
          'Increase your income and customer happiness by offering related services, like post-groom paw massages, that keep them coming back.',
      },
    ],
  },
];

export const tuftFaqsConfig = [
  {
    question: 'Is Pata better than Tuft?',
    answer: () => (
      <>
        <p>
          Do dogs love pup cups? (Yes!) Pata booking software is simple to navigate, connects you to local pet owners
          through our online marketplace, and offers subscription-free access to features, no matter your business size
          or speciality.
        </p>

        <p>
          Not only are we loved by dog groomers across the UK, but our flexible tools and easy-to-use platform are
          designed for all pet care professionals. It’s thanks to our in-house team that we can quickly respond to
          feedback and adapt our app to fit your needs.
        </p>
      </>
    ),
  },
  {
    question: 'Does Pata have a free plan?',
    answer: () => (
      <>
        <p>
          Absolutely! In fact, Pata is subscription-free and offers you unlimited access to the tools you need to make
          admin easy. That means no upfront or monthly costs, so you can try our booking platform at your own pace.
        </p>

        <p>
          If you want to expand your business further, we offer additional SMS notifications and{' '}
          <a href={`${VITE_WEB_BASE_URL}/business/pata-pay`} target="_blank">
            payment processing
          </a>{' '}
          at low fees, but these features are entirely optional.
        </p>
      </>
    ),
  },
  {
    question: 'Can I import my data from Tuft?',
    answer: () => (
      <>
        <p>
          Yes, we’re here to make the transition as easy as possible and can help import key data from Tuft into Pata’s
          booking software. This includes client details, appointments, services, and staff schedules.
        </p>
      </>
    ),
  },
  {
    question: 'Is Pata suitable for solo groomers and small teams?',
    answer: () => (
      <>
        <p>
          Pata can help businesses of any size to thrive and simplify their admin, from small teams to multi-location
          grooming salons. With no upfront or monthly fees, our pet care software is perfect for solo or mobile groomers
          who are looking for the same powerful tools that larger businesses use.
        </p>

        <p>
          From 24/7 online bookings and calendar management to automated client communications, our flexible and
          intuitive features are designed to save you time and help grow your business.
        </p>
      </>
    ),
  },
  {
    question: 'Does Pata support online payments and deposits?',
    answer: () => (
      <>
        <p>
          With{' '}
          <a href={`${VITE_WEB_BASE_URL}/business/pata-pay`} target="_blank">
            PataPay
          </a>
          , it’s easy to take upfront payments and deposits online to help reduce no-shows and protect yourself against
          late cancellations. For an extra level of security, you can store card details for future no-show fees.
        </p>

        <p>
          If you have clients who prefer to pay in person, we also offer a card reader. What’s more, every transaction
          is integrated into our booking platform and securely tracked in real time, so you stay in control of your
          income.
        </p>
      </>
    ),
  },
  {
    question: 'What types of pet care businesses can use Pata?',
    answer: () => (
      <>
        <p>
          Pata booking platform is designed for all pet care professionals — from groomers, walkers and sitters to dog
          trainers, vets and more.
        </p>

        <p>
          Whether you’re a solo dog groomer who also offers boarding or you run a busy training school, Pata helps pros
          like you to take bookings, manage schedules, minimise no-shows, and grow your business with one simple app.
        </p>
      </>
    ),
  },
];

const TestimoninalTitle = () => {
  return (
    <TextWithHighlight>
      <h2>What groomers think of our Tuft alternative…</h2>
    </TextWithHighlight>
  );
};

const TestimoninalSubTitle = () => {
  return (
    <p style={{ maxWidth: '850px' }}>
      When it’s time to compare pet grooming software, there’s no better judge than groomers themselves! Here's why pet
      pros love using Pata to manage bookings, reduce admin and grow their pet business.
    </p>
  );
};

const SignUpProcessTitle = () => {
  return (
    <SignUpProcessTitleContainer>
      <h2>Join the community of UK pet groomers growing their businesses with Pata, the Tuft software alternative.</h2>
      <p>
        Looking for grooming software with deposits and other features to reduce no-shows and save time? Try Pata, the
        booking platform that makes bookings easy for pros like you.
      </p>
    </SignUpProcessTitleContainer>
  );
};

export default function BusinessPataVsTuft() {
  return (
    <BusinessCompetitor
      heroColor="#f86e6a"
      faqsConfig={tuftFaqsConfig}
      highlevelBenefitsConfig={highlevelBenefitsConfig}
      signUpProcessTitle={SignUpProcessTitle}
      testimoninalTitle={TestimoninalTitle}
      testimoninalSubTitle={TestimoninalSubTitle}
      testimoninalConfig={TuftTestimoninalConfig}
      heroConfig={tuftHeroConfig}
      featuresTiles={tuftFeaturesTiles}
      featuresTitle={'Pata vs Tuft: What makes our booking software different?'}
    />
  );
}
